import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useChatSession } from '~/features/chatbot/hooks/useChatSession';
import { useDeleteSession } from '~/features/chatbot/hooks/useDeleteSession';
import { useGetSessionsQuery, useLazyGetSessionByIdQuery } from '~/api/chatbot/Chatbot.api';
import { CHATBOT_CONFIG } from '~/features/chatbot/constants/chatbotConfig';
import ConversationPanel from './Conversation/ConversationPanel';
import DeleteSessionDialog from './SessionList/DeleteSessionDialog';
import { transformApiMessages } from '~/features/chatbot/utils/transformApiMessages';
// @ts-ignore
import sogardAvatar from '~/features/chatbot/assets/sogard-avatar.png';

interface ChatbotPopupProps {
    isOpen: boolean;
    onClose: () => void;
}

type PopupView = 'list' | 'conversation';

const formatRelativeTime = (dateStr: string): string => {
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMinutes < 60) return `${diffMinutes}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return `${Math.floor(diffDays / 7)}w`;
};

const ChatbotPopup = ({ isOpen, onClose }: ChatbotPopupProps) => {
    const [activeView, setActiveView] = useState<PopupView>('list');
    const [sessionToDelete, setSessionToDelete] = useState<string | null>(null);
    const { sessionId, messages, isLoading, error, sendMessage, startNewSession, loadSession, setSessionError } = useChatSession();
    const { deleteSession } = useDeleteSession();
    const { data: sessions = [], isLoading: isLoadingSessions, error: sessionsError } = useGetSessionsQuery();
    const [fetchSessionById] = useLazyGetSessionByIdQuery();
    const intl = useIntl();

    if (!isOpen) {
        return null;
    }

    const handleSend = (text: string) => {
        sendMessage(text);
    };

    const handleQuestionClick = (question: string) => {
        sendMessage(question);
    };

    const handleNewQuestion = () => {
        startNewSession();
        setActiveView('conversation');
    };

    const handleSessionClick = async (clickedSessionId: string) => {
        setActiveView('conversation');
        try {
            const sessionDetail = await fetchSessionById(clickedSessionId).unwrap();
            const loadedMessages = transformApiMessages(clickedSessionId, sessionDetail.messages);
            loadSession(clickedSessionId, loadedMessages);
        } catch {
            setSessionError(intl.formatMessage({ id: 'intl-msg:error.chatbot.loadSessionFailed' }));
        }
    };

    const handleBack = () => {
        setActiveView('list');
    };

    const handleDeleteClick = (e: React.MouseEvent, sessionId: string) => {
        e.stopPropagation();
        setSessionToDelete(sessionId);
    };

    const handleDeleteConfirm = async () => {
        if (sessionToDelete) {
            await deleteSession(sessionToDelete);
            setSessionToDelete(null);
        }
    };

    const handleDeleteCancel = () => {
        setSessionToDelete(null);
    };

    const isNewSession = sessionId === '';
    const hasSessions = sessions.length > 0;

    return (
        <div
            className="position-fixed bg-white rounded-large shadow-default display-flex flex-column overflow-hidden"
            style={{
                bottom: '90px',
                right: '24px',
                width: '380px',
                height: '520px',
                zIndex: 1500,
            }}
            data-testid="chatbot-popup"
            role="dialog"
            aria-label="Chatbot"
        >
            <div className="display-flex align-items-center justify-content-between padding-15 padding-left-20 padding-right-20 border-bottom-only">
                <h5 className="margin-0">{CHATBOT_CONFIG.headerTitle}</h5>
                <button
                    className="btn btn-link btn-icon-only padding-0"
                    onClick={onClose}
                    data-testid="chatbot-popup-close"
                    aria-label="Close chatbot"
                >
                    <span className="rioglyph rioglyph-remove text-size-16" aria-hidden="true" />
                </button>
            </div>

            <div className="flex-1-1 overflow-hidden display-flex flex-column">
                {activeView === 'conversation' ? (
                    <>
                        <div className="display-flex align-items-center padding-10 padding-left-15 padding-right-15 border-bottom-only">
                            <button
                                className="btn btn-link btn-sm padding-0 margin-right-10"
                                onClick={handleBack}
                                data-testid="chatbot-popup-back"
                                aria-label="Back to conversations"
                            >
                                <span className="rioglyph rioglyph-chevron-left text-size-16" aria-hidden="true" />
                            </button>
                            <img
                                src={sogardAvatar}
                                alt={CHATBOT_CONFIG.name}
                                className="rounded-circle margin-right-10"
                                style={{ width: '36px', height: '36px', objectFit: 'cover', border: 'none' }}
                            />
                            <span className="text-size-14 font-weight-bold flex-1-1">{CHATBOT_CONFIG.name}</span>
                        </div>
                        <div className="flex-1-1 overflow-hidden">
                            <ConversationPanel
                                messages={messages}
                                isLoading={isLoading}
                                error={error}
                                isNewSession={isNewSession}
                                onSend={handleSend}
                                onQuestionClick={handleQuestionClick}
                                disabled={isLoading}
                            />
                        </div>
                    </>
                ) : (
                    <div className="flex-1-1 display-flex flex-column overflow-hidden">
                        {isLoadingSessions ? (
                            <div className="flex-1-1 display-flex align-items-center justify-content-center">
                                <span className="spinner spinner-default" aria-label="Loading sessions" />
                            </div>
                        ) : sessionsError ? (
                            <div className="flex-1-1 display-flex flex-column align-items-center justify-content-center padding-20">
                                <span className="rioglyph rioglyph-error-sign text-size-30 text-color-danger margin-bottom-15" aria-hidden="true" />
                                <p className="text-size-14 text-color-dark margin-bottom-5">
                                    <FormattedMessage id="intl-msg:error.chatbot.loadSessionsTitle" />
                                </p>
                                <p className="text-size-12 text-color-gray margin-bottom-20">
                                    <FormattedMessage id="intl-msg:error.chatbot.loadSessionsDetails" />
                                </p>
                                <button
                                    className="btn btn-primary btn-sm"
                                    style={{ borderRadius: '20px', paddingLeft: '24px', paddingRight: '24px' }}
                                    onClick={handleNewQuestion}
                                    data-testid="chatbot-popup-new-question"
                                >
                                    <span className="rioglyph rioglyph-plus-sign margin-left-5" aria-hidden="true" />
                                    <FormattedMessage id="intl-msg:chatbot.newChat" />
                                </button>
                            </div>
                        ) : hasSessions ? (
                            <div className="flex-1-1 display-flex flex-column overflow-hidden">
                                <div className="flex-1-1 overflow-y-auto padding-left-20 padding-right-20 padding-top-10">
                                    {sessions.map((session, index) => (
                                        <div
                                            key={session.id}
                                            className={`padding-top-15 padding-bottom-15 cursor-pointer display-flex align-items-center${index < sessions.length - 1 ? ' border border-bottom-only border-color-lighter' : ''}`}
                                            onClick={() => handleSessionClick(session.id)}
                                            role="button"
                                            tabIndex={0}
                                            data-testid={`popup-session-${session.id}`}
                                        >
                                            <img
                                                src={sogardAvatar}
                                                alt={CHATBOT_CONFIG.name}
                                                className="rounded-circle margin-right-15"
                                                style={{ width: '40px', height: '40px', objectFit: 'cover', border: 'none', flexShrink: 0 }}
                                            />
                                            <div className="flex-1-1 overflow-hidden">
                                                <div className="text-size-14 font-weight-bold text-overflow-ellipsis white-space-nowrap overflow-hidden">
                                                    {session.title}
                                                </div>
                                                <div className="text-size-12 text-color-gray margin-top-3">
                                                    {new Date(session.lastMessageAt).toLocaleDateString()}
                                                </div>
                                            </div>
                                            <span className="text-size-12 text-color-gray white-space-nowrap margin-left-10">
                                                {formatRelativeTime(session.lastMessageAt)}
                                            </span>
                                            <button
                                                className="btn btn-link btn-icon-only padding-0 margin-left-10"
                                                onClick={(e) => handleDeleteClick(e, session.id)}
                                                aria-label="Delete session"
                                                data-testid={`delete-session-${session.id}`}
                                            >
                                                <span className="rioglyph rioglyph-trash text-size-14 text-color-gray" aria-hidden="true" />
                                            </button>
                                        </div>
                                    ))}
                                </div>

                                <div className="padding-15 display-flex justify-content-center border-top-only">
                                    <button
                                        className="btn btn-primary btn-sm"
                                        style={{ borderRadius: '20px', paddingLeft: '24px', paddingRight: '24px' }}
                                        onClick={handleNewQuestion}
                                        data-testid="chatbot-popup-new-question"
                                    >
                                        <span className="rioglyph rioglyph-plus-sign margin-left-5" aria-hidden="true" />
                                        <FormattedMessage id="intl-msg:chatbot.newChat" />
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="flex-1-1 display-flex flex-column align-items-center justify-content-center padding-20">
                                <img
                                    src={sogardAvatar}
                                    alt={CHATBOT_CONFIG.name}
                                    className="rounded-circle margin-bottom-15"
                                    style={{ width: '64px', height: '64px', objectFit: 'cover', border: 'none' }}
                                />
                                <p className="text-size-14 text-color-dark margin-bottom-5">
                                    <FormattedMessage id="intl-msg:chatbot.emptyState.title" />
                                </p>
                                <p className="text-size-12 text-color-gray margin-bottom-20">
                                    <FormattedMessage id="intl-msg:chatbot.emptyState.subtitle" />
                                </p>
                                <button
                                    className="btn btn-primary btn-sm"
                                    style={{ borderRadius: '20px', paddingLeft: '24px', paddingRight: '24px' }}
                                    onClick={handleNewQuestion}
                                    data-testid="chatbot-popup-new-question"
                                >
                                        <span className="rioglyph rioglyph-plus-sign margin-left-5" aria-hidden="true" />
                                        <FormattedMessage id="intl-msg:chatbot.newChat" />
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <DeleteSessionDialog
                isOpen={sessionToDelete !== null}
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
            />
        </div>
    );
};

export default ChatbotPopup;
